import React, { createContext } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import {
    get as _get,
    set as _set,
    debounce as _debounce,
    isEmpty as _isEmpty,
} from 'lodash';
import useIdleMonitor from './idle-monitor-service';
import { GenerateCodeChallengeS256, InitCase } from '../libraries/helpers';
import PropTypes from 'prop-types';
import Moment from 'moment';

const profileKey = 'auth';
const AuthURL = '/authorested';
const ClientID = 'CMCGUI';
const RedirectURI = `${window.location.origin}/oauth/callback`;
const ErrorbackURI = `${window.location.origin}/oauth/errorback`;
const SAMLLoginURL = `${AuthURL}/samlauth/auth`;

class AuthService {
    constructor () {
        if (!AuthService.instance) {
            AuthService.instance = this;
            this.refreshMux = new Mutex();
            // save access token in memory, if lost we'll use the refresh token to get a new one
            this.access_token = '';
            this.permissions = {
                _constants: {},
                _descriptions: {},
            };

            // This makes me sad, so does the alternative

            // const descriptionsRequest = new XMLHttpRequest();
            // const constantsRequest = new XMLHttpRequest();

            // descriptionsRequest.open('GET', '/api/auth/-descriptions', false);
            // try {
            //     descriptionsRequest.send();
            //     if (descriptionsRequest.status !== 200) {
            //         console.error('Unable to get permission descriptions');
            //     } else {
            //         const r = JSON.parse(descriptionsRequest.response);
            //         const c = {};
            //         Object.keys(r).forEach((x) => {
            //             c[BigInt(x)] = r[x];
            //         });
            //         this.permissions._descriptions = c;
            //     }
            // } catch (err) {
            //     console.error(err);
            // }

            // constantsRequest.open('GET', '/api/auth/-constants', false);
            // try {
            //     constantsRequest.send();
            //     if (constantsRequest.status !== 200) {
            //         console.error('Unable to get permission constants');
            //     } else {
            //         const r = JSON.parse(constantsRequest.response);
            //         const c = {};
            //         Object.keys(r).forEach((x) => {
            //             c[r[x]] = x + '';
            //         });
            //         this.permissions._constants = c;
            //     }
            // } catch (err) {
            //     console.error(err);
            // }

            // This is the alternative :(
            this.getPermDescriptions = () => {
                return fetch('/api/auth/-descriptions', {
                    method: 'GET',
                    responseType: 'application/json',
                }).then(descriptionResponse => {
                    descriptionResponse.json().then(res => {
                        const c = {};
                        // defense against API developer changing permission level case
                        Object.keys(res).forEach((x) => {
                            c[BigInt(x)] = Object.entries(res[x])
                                .reduce((a, [k, v]) => ({ ...a, [InitCase(k)]: v }), {});
                        });
                        this.permissions._descriptions = c;
                    });
                }, err => {
                    console.error('Unable to get permission descriptions ::: ', err);
                });
            };

            this.getPermConstants = () => {
                return fetch('/api/auth/-constants', {
                    method: 'GET',
                    responseType: 'application/json',
                }).then(constantsResponse => {
                    constantsResponse.json().then(res => {
                        const c = {};
                        Object.keys(res).forEach((x) => {
                            c[res[x]] = x + '';
                        });
                        this.permissions._constants = c;
                    });
                }, err => {
                    console.error('Unable to get permission constants ::: ', err);
                });
            };
        }

        // PKCE
        this.verifier = sessionStorage.getItem('verifier');
        this.state = sessionStorage.getItem('state');

        this.isAvailable = () => {
            return fetch(`${AuthURL}/oauth/available`, {
                method: 'GET',
            }).then(ResponseHandler)
                .then(res => {
                    if (_get(res, 'available')) {
                        return res;
                    }
                    return Promise.reject(new Error('Auth server not available'));
                });
        };

        this.maybeNewCodeChallenge = () => {
            return this.verifier ? GenerateCodeChallengeS256(this.verifier) : this.newCodeChallenge();
        };

        this.newCodeChallenge = () => {
            const v = Math.random().toString(36).substring(2, 43);
            this.verifier = v;
            // store in session storage (must survive a page refresh)
            sessionStorage.setItem('verifier', v);
            return GenerateCodeChallengeS256(v);
        };

        this.getCodeVerifier = () => {
            // code is one time use
            sessionStorage.removeItem('verifier');
            const ret = this.verifier;
            this.verifier = undefined;
            return ret;
        };

        this.getState = () => {
            // state is one time use
            sessionStorage.removeItem('state');
            const ret = this.state;
            this.state = undefined;
            return ret;
        };

        this.newState = () => {
            const s = Math.random().toString(36).substring(2, 15);
            this.state = s;
            // store in session storage
            sessionStorage.setItem('state', s);
            return s;
        };

        this.maybeNewState = () => {
            return this.state || this.newState();
        };

        this.loggedIn = () => {
            return !!this.access_token;
        };

        // LOGIN STEP 2: exchange the code for a token
        this.getTokens = ({ code, state, codeVerifier, redirectURI }) => {
            const expectedState = this.getState();

            if (state !== expectedState) {
                return Promise.reject(new Error(`state mismatch, ${state} !== ${expectedState}`));
            }

            const params = {
                client_id: ClientID,
                grant_type: 'authorization_code',
                scope: 'all',
                cmc_home: RedirectURI,
                // redirect_uri: RedirectURI,
                errorback_uri: ErrorbackURI,
                code,
                code_verifier: this.getCodeVerifier(),
            };
            return fetch(
                `${AuthURL}/oauth/token`,
                {
                    method: 'POST',
                    body: ParamsSerializer(params),
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                })
                .then(res => {
                    if (!res.ok) {
                        return Promise.reject(res);
                    }
                    return res;
                }, Promise.reject)
                .then(ResponseHandler, Promise.reject)
                .then(this.finishLogin, Promise.reject)
                .catch(Promise.reject);
            // .catch(this.boot);
        };

        // LOGIN STEP 3: get user info
        this.finishLogin = (tokens) => {
            this.access_token = tokens.access_token;
            return fetch('/api/users/-self/', {
                method: 'GET',
                headers: { Authorization: `Bearer ${tokens.access_token}` }
            })
                .then(ResponseHandler)
                .then(res => ({ user: res.object, tokens }))
                .then(res => {
                    return this.setSession(res.tokens, res.user).then(() => res);
                });
        };

        // don't stash access token
        this.stashSession = (data) => {
            const { token, ...rest } = (data || {});
            const { token: etoken, ...existing } = this.getAuth();

            // no need to wipe everything out, license takes a minutes to verify - let's use the existing until that's back
            localStorage.setItem(profileKey, JSON.stringify({ ...existing, ...rest }));
        };

        this.getAuth = () => {
            try {
                const ret = JSON.parse(localStorage.getItem(profileKey));
                ret.token = this.access_token;
                return ret;
            } catch (err) {
                return false;
            }
        };

        this.logout = () => {
            const auth = this.getAuth();

            const token = _get(auth, 'token');
            if (token) {
                fetch(`${AuthURL}/logout`, {
                    method: 'POST',
                    headers: AuthorizationHeader(token),
                });
            }
            this.clearSession();
        };

        this.clearSession = () => {
            localStorage.removeItem(profileKey);
            UISettings.clear();
            this.license = undefined;
            this.access_token = '';
        };

        this.boot = (url, token) => {
            // do not logout unless explicitly told to, it will revoke all tokens
            this.clearSession();
            location.assign('/signin');
            // window.location.reload();
        };

        // this will fail if the refresh token is not good anymore
        this.refresh = async (oldToken) => {
            const refreshToken = _get(this.getAuth(), 'refresh_token');
            const user = _get(this.getAuth(), 'user');
            if (!refreshToken || !user) return Promise.reject(new Error('no refresh token'));

            // guard against concurrent calls to refresh token
            this.counter = this.counter || 0;
            const unlock = await this.refreshMux.lock();
            const newToken = _get(this.getAuth(), 'token');
            if (newToken !== oldToken) {
                unlock();
                return Promise.resolve(newToken);
            }
            const params = {
                client_id: ClientID,
                grant_type: 'refresh_token',
                // redirect_uri: RedirectURI,
                cmc_home: RedirectURI,
                errorback_uri: ErrorbackURI,
                refresh_token: refreshToken,
            };
            this.counter++;
            return fetch(
                `${AuthURL}/oauth/token`,
                {
                    method: 'POST',
                    body: ParamsSerializer(params),
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                })
                .then(ResponseHandler)
                .then(res => {
                    this.setSession(res, user);
                    return res.access_token;
                }).finally(newToken => {
                    unlock();
                    return newToken;
                });
        };

        // stash user and refresh token
        this.setSession = (tokens, user) => {
            this.access_token = tokens.access_token;
            this.refresh_token = tokens.refresh_token;
            this.stashSession({
                refresh_token: tokens.refresh_token,
                user,
                timestamp: new Date().getTime(),
                perms: (user.permissions || {
                    admin: 0,
                    delete: 0,
                    override: 0,
                    read: 0,
                    write: 0,
                })
            });
            return this.validateLicense();
        };

        this.setPassword = (username, data) => {
            return FetchAPI({
                method: 'PUT',
                url: `users/${username}/set-password`,
                body: data,
            }).then(res => {
                const info = JSON.parse(localStorage.getItem(profileKey));
                info.user = { ...info.user, password_expired: false };
                this.stashSession(info);
                return res;
            });
        };

        this.resetPassword = (username) => {
            return FetchAPI({
                method: 'PUT',
                url: `users/${username}/reset-password`,
            });
        };

        this.getPasswordToken = (username) => {
            return FetchAPI({
                method: 'PUT',
                url: `users/${username}/password-token`,
            });
        };

        // updates local storage and server
        this.prevProfileComponent = undefined;
        this.updateProfileEventually = _debounce(component => {
            const auth = this.getAuth();
            if (!auth) return;
            const data = _get(auth.user, `data.profile.${component}`);
            FetchAPI({
                method: 'PUT',
                url: `users/profile/${component}/`,
                body: data
            }).then(res => {}, err => console.error(err));
            this.prevProfileComponent = undefined;
        }, 1100);
        this.updateProfile = (component, data) => {
            const auth = this.getAuth();
            if (!auth) return;
            _set(auth.user, `data.profile.${component}`, data);
            this.stashSession(auth);
            if (this.prevProfileComponent !== component) {
                // update previous component
                this.updateProfileEventually.flush();
            }
            this.prevProfileComponent = component;
            this.updateProfileEventually(component);
        };

        // grab from current session
        this.getProfile = (component) => {
            const auth = this.getAuth();
            if (!auth) return;

            return _get(auth, `user.data.profile.${component}`, {});
        };

        this.preferredAuthSource = () => {
            // read from local storage
            try {
                const as = JSON.parse(localStorage.getItem('authSource'));
                return as || 'brc';
            } catch (err) {
                return 'brc';
            }
        };

        this.setPreferredSource = src => {
            localStorage.setItem('authSource', src);
        };

        this.validateLicense = refresh => {
            return FetchAPI({ method: 'GET', url: 'license/validate' }).then(res => {
                const auth = this.getAuth();
                if (!auth) return false;
                if (res.valid) {
                    auth.license = res;
                    this.stashSession(auth);
                }
                return res.valid;
            }, err => {
                console.error('cannot fetch license', err);
                return false;
            }).catch(err => {
                console.error('cannot fetch license', err);
                return false;
            });
        };

        this.acceptEula = dontShow => {
            const auth = this.getAuth();
            _set(auth, 'user.data.profile.hide_eula', dontShow);
            _set(auth, 'user.data.profile.eula_accept', Moment().utc().toISOString());
            FetchAPI({
                method: 'PUT',
                url: `users/${auth.user.username}`,
                body: auth.user
            }).catch(err => {
                console.error('cannot accept Eula', err);
                // remove
                _set(auth, 'user.data.profile.eula_accept', '');
                this.stashSession(auth);
            });
            // don't wait for response to update the cookie
            this.stashSession(auth);
        };

        this.updateTheme = theme => {
            const auth = this.getAuth();
            _set(auth, 'user.data.profile.colorscheme', theme);
            this.stashSession(auth);
        };

        this.updateDates = formats => {
            const auth = this.getAuth();
            _set(auth, 'user.data.profile.date', formats);
            this.stashSession(auth);
        };

        this.perm = (p, requireAll) => {
            if (_isEmpty(this.permissions._constants)) return;
            if (!Object.prototype.toString.call(p).includes('Object')) return;
            const auth = this.getAuth();
            if (!auth) return;

            let match = Boolean(requireAll);

            const matchPermission = (a, name, pe) => {
                if (requireAll && !match) {
                    return;
                } else if (!requireAll && match) {
                    return;
                }

                if (Object.prototype.toString.call(pe).includes('Object')) {
                    for (const x in pe) {
                        if (Object.prototype.hasOwnProperty.call(pe, x)) {
                            matchPermission(a, x, pe[x]);
                        }
                    }
                } else {
                    for (let i = pe.length; i--;) {
                        if (a[pe[i]] && this.permissions._constants[name] !== undefined) {
                            if (BigInt(a[pe[i]]) & BigInt(this.permissions._constants[name])) {
                                match = true;
                                if (!requireAll) break;
                            } else {
                                match = false;
                                if (requireAll) break;
                            }
                        }
                    }
                }
            };

            if (Object.prototype.toString.call(p).includes('Object')) {
                for (const x in p) {
                    if (Object.prototype.hasOwnProperty.call(p, x)) {
                        matchPermission(auth.perms, x, p[x]);
                    }
                }
            }

            return match;
        };

        this.permissions.get = (permSet) => {
            const empty = { read: false, write: false, delete: false, override: false, admin: false };
            if (_isEmpty(this.permissions._constants)) return empty;
            if (this.permissions._constants[permSet] === undefined) return empty;
            const perms = { ...empty, ...(this.getAuth().perms || {}) };
            const p = {
                read: Boolean(BigInt(perms.read) & BigInt(this.permissions._constants[permSet])),
                write: Boolean(BigInt(perms.write) & BigInt(this.permissions._constants[permSet])),
                delete: Boolean(BigInt(perms.delete) & BigInt(this.permissions._constants[permSet])),
                override: Boolean(BigInt(perms.override) & BigInt(this.permissions._constants[permSet])),
                admin: Boolean(BigInt(perms.admin) & BigInt(this.permissions._constants[permSet])),
            };

            return p;
        };

        this.permissions.getDescription = (permSet, perm) => {
            if (Object.prototype.hasOwnProperty.call(this.permissions._descriptions, this.permissions._constants[permSet])) {
                return this.permissions._descriptions[this.permissions._constants[permSet]][perm];
            }
            return {};
        };

        return AuthService.instance;
    }
}
const Auth = new AuthService();

// doesn't exactly belong in this file, but contains auth settings
// and this file is at the top of the dep list
class UISettingsService {
    constructor () {
        if (!UISettingsService.instance) {
            UISettingsService.instance = this;
            this.uiSettingsMux = new Mutex();
        }
        // only want to call this guy one at a time
        this.fetchUI = async refresh => {
            const unlock = await this.uiSettingsMux.lock();
            if (!refresh && this.settings) {
                unlock();
                return Promise.resolve(this.settings);
            }
            return FetchAPI({
                method: 'GET',
                url: 'system/-ui',
            }).then(res => {
                this.settings = res;
                return this.settings;
            }, err => {
                console.error('Unable to get GUI settings', err);
            }).finally(() => unlock());
        };
        this.availableAuthSource = async () => {
            const s = await this.fetchUI();
            return _get(s, 'auth.okta.enabled', false) ? 'okta'
                : _get(s, 'auth.saml.enabled', false) ? 'saml'
                    : '';
        };
        this.messages = async () => {
            const s = await this.fetchUI();
            return s.messages || {};
        };
        this.lookups = async refresh => {
            const s = await this.fetchUI(refresh);
            return s.lookups || [];
        };
        this.filehashLookups = async refresh => {
            const s = await this.fetchUI(refresh);
            return s.file_lookups || [];
        };
        this.clear = () => delete this.settings;

        return UISettingsService.instance;
    }
}
const UISettings = new UISettingsService();

class SupportSettingsService {
    constructor () {
        if (!SupportSettingsService.instance) {
            SupportSettingsService.instance = this;
            this.supportSettingsMux = new Mutex();
        }
        // only want to call this guy one at a time
        this.fetchSupport = async refresh => {
            const unlock = await this.supportSettingsMux.lock();
            if (!refresh && this.settings) {
                unlock();
                return Promise.resolve(this.settings);
            }
            return FetchAPI({
                method: 'GET',
                url: 'system/-support',
            }).then(res => {
                this.settings = res;
                return this.settings;
            }).finally(() => unlock());
        };
        this.backtesting = async () => {
            const s = await this.fetchSupport();
            return s.backtesting || false;
        };
        this.backtestingUI = async () => {
            const s = await this.fetchSupport();
            return s.backtestingUI || false;
        };
        this.clear = () => delete this.settings;

        return SupportSettingsService.instance;
    }
}
const SupportSettings = new SupportSettingsService();

class SAMLAuthService {
    constructor () {
        if (!SAMLAuthService.instance) {
            SAMLAuthService.instance = this;
        }

        // step 1 in saml.js
        // `${AuthURL}/samlauth/auth`;

        // Step 2
        this.GetSession = () => {
            return fetch(`${AuthURL}/samlauth/valid`, {
                method: 'GET',
            }).then(ResponseHandler, err => {
                console.log('saml auth not available', err);
                return Promise.reject('SAML not available');
            }).catch(err => {
                console.error('SAML not available exception', err);
                return Promise.reject('SAML not available');
            });
        };

        // Step 3
        this.Login = () => {
            return fetch(`${AuthURL}/samlauth/login`, {
                method: 'POST',
                body: { ClientID, RedirectURI }
            })
                .then(ResponseHandler)
                .then(Auth.finishLogin, Auth.boot)
                .catch(Auth.boot);
        };

        return SAMLAuthService.instance;
    }
}
const SAMLAuth = new SAMLAuthService();

class OktaAuthService {
    constructor () {
        if (!OktaAuthService.instance) {
            OktaAuthService.instance = this;
        }

        this.Login = (tokens) => {
            return fetch(`${AuthURL}/okta/login`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${tokens.accessToken.accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(tokens.idToken),
            })
                .then(ResponseHandler)
                .then(r => {
                    return fetch('/api/users/-self/', {
                        method: 'GET',
                        headers: { Authorization: `Bearer ${tokens.accessToken.accessToken}` }
                    })
                        .then(ResponseHandler)
                        .then(res => ({
                            user: res.object,
                            tokens: {
                                access_token: tokens.accessToken.accessToken,
                                token_type: 'bearer',
                            }
                        }));
                })
                .then(res => {
                    return Auth.setSession(res.tokens, res.user).then(() => res);
                });
        };

        return OktaAuthService.instance;
    }
}
const OktaAuth = new OktaAuthService();

const AuthenticatedRoute = (props) => {
    const auth = Auth.getAuth();
    const timeout = _get(auth, 'user.session_timeout', 0);

    useIdleMonitor({
        timeout: timeout * 60000,
        onTimeout: Auth.boot,
    });
    return auth
        ? <Outlet />
        : <Navigate to='/signin' />;
};

// if no auth goto unauthorized
// else if no license go to license
// else if password expired go to password page
// else if hasn't accepted eula go to eula page
// else route
const PermRoute = ({ perm, element, nochecks, ...rest }) => {
    const auth = Auth.getAuth();
    const user = _get(auth, 'user', {});

    const p = !perm ? {} : Object.prototype.toString.call(perm).includes('Object') ? perm : {};
    const license = rest.nochecks || _get(auth, 'license.valid', false);
    const password = rest.nochecks || _get(user, 'password_expired', true) === false;
    const eula = rest.nochecks || _get(user, 'data.profile.eula_accept', false);
    // console.debug('license', license, 'password', password, 'eula', eula, auth);

    return nochecks ? <Outlet />
        : !Auth.perm(p) ? <Navigate to='/unauthorized' />
            : !license && process.env.BRICATA_APPLIANCE_TYPE !== 'mc2' ? <Navigate to='/system/license' {...rest} />
                : !password ? <Navigate to={`/system/users_roles/password/${user.username}`} {...rest} />
                    : !eula ? <Navigate to={'/support/eula'} {...rest} />
                        : <Outlet />;
};
PermRoute.propTypes = {
    perm: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
    element: PropTypes.node,
    nolicense: PropTypes.bool,
    nochecks: PropTypes.bool,
};

// Add the auth header with a jwt token
function AuthorizationHeader (token) {
    return token ? { Authorization: `Bearer ${token}` } : {};
}

// process json or text response
function ResponseHandler (response) {
    if (!response) return '';
    return response.text().then(text => {
        let data = text;
        try {
            data = text && JSON.parse(text);
        } catch (err) {
            // response isn't json
            if ((text || '').trim() !== 'null') {
                data = text;
            }
        }
        if (!response.ok) {
            // just in case text is empty
            console.debug('response handler text', text);
            console.debug('response handler data', data);
            console.debug('response handler response', response);

            const error = text || (data ? data.message : response);
            return Promise.reject(error);
        }
        return data;
    });
}

// process arrayBuffer
function ArrayBufferHandler (response) {
    if (!response.ok) {
        return Promise.reject(response);
    }
    return response.arrayBuffer().then(buffer => {
        // unreachable code FTW
        if (!response.ok) {
            const error = (buffer && buffer.message) || response;
            return Promise.reject(error);
        }
        return buffer;
    });
}

const ParamsSerializer = (params) => {
    return Object
        .entries(params || {})
        .filter(([k, v]) => (v !== undefined))
        .reduce((a, [k, v]) => {
            if (Array.isArray(v)) {
                return [...a, v.map(vv => `${encodeURIComponent(k)}=${encodeURIComponent(vv)}`).join('&')];
            }
            return [...a, `${encodeURIComponent(k)}=${encodeURIComponent(v)}`];
        }, [])
        .join('&');
};

const FormatError = (err) => {
    let error = err;
    try {
        error = err && JSON.parse(err);
    } catch (e) {
        return err;
    }

    return error;
};

// this guy is a wrapper for API calls.
// it adds the auth header and it processes response
// if unauthorized it'll attempt to refresh the token and try again
// use notagain = true if you don't want to refresh on unauthorized
// accepts an AbortController.signal
const FetchAPI = (params) => {
    const { method, url, body, notagain, cache, signal, plain, responseType, newtoken } = params;
    const token = newtoken || _get(Auth.getAuth(), 'token', '');
    try {
        return fetch(`/api/${url}`, {
            method: method || 'GET',
            body: (method || 'GET').toUpperCase() !== 'GET' ? plain ? body : JSON.stringify(body) : undefined,
            cache: cache ? 'force-cache' : 'default',
            headers: AuthorizationHeader(token),
            responseType,
            signal
        })
            .then(responseType === 'arraybuffer' ? ArrayBufferHandler : ResponseHandler, error => {
                if (signal && signal.aborted) {
                    return Promise.resolve();
                }
                return Promise.reject(FormatError(error));
            })
            .catch(error => {
                // This is dirty but changing a password bails as a 401 :(
                // if we run into a second call that can 401 - let's add an optional dont-bail param
                if ([401, 403].indexOf(error.status) !== -1 && !error.url.match('set-password')) {
                    // escape hatch
                    if (notagain) {
                        console.error('not again', url, token);
                        return Auth.boot(url, token);
                    }

                    // if the refresh token still has some life repeat the failed API call
                    // otherwise boot the user out
                    return Auth.refresh(token)
                        .then(newtoken => FetchAPI({ ...params, notagain: true, newtoken }))
                        .catch(err => {
                            if (error.name === 'AbortError') {
                                return Promise.reject(error);
                            }
                            Auth.boot(url, token);
                        });
                } else if (signal && signal.aborted) {
                    return Promise.resolve();
                }
                return Promise.reject(FormatError(error));
            });
    } catch (err) {
        return Promise.reject(err);
    }
};

// https://stackoverflow.com/a/51086893/5895550
function Mutex () {
    let current = Promise.resolve();
    this.lock = () => {
        let _resolve;
        const p = new Promise(resolve => {
            _resolve = () => resolve();
        });
        // Caller gets a promise that resolves when the current outstanding
        // lock resolves
        const rv = current.then(() => _resolve);
        // Don't allow the next request until the new promise is done
        current = p;
        // Return the new promise
        return rv;
    };
}

// for convenience
const User = () => {
    const auth = Auth.getAuth();
    return auth ? auth.user : {};
};

// context to get/update user profile for each page
const ProfileContext = createContext({
    updateProfile: Auth.updateProfile,
    getProfile: Auth.getProfile,
    perm: Auth.perm
});

export {
    Auth as default,
    SAMLAuth,
    OktaAuth,
    AuthenticatedRoute,
    PermRoute,
    User,
    FetchAPI,
    ParamsSerializer,
    Mutex,
    ProfileContext,
    UISettings,
    SupportSettings,
    ResponseHandler,
    ClientID,
    AuthURL,
    RedirectURI,
    ErrorbackURI,
    SAMLLoginURL,
};
